/**
 * CollapsedBar - свёрнутое состояние виджета (56px высота)
 *
 * Структура: [Иконка] [Title] ... [Voice?] [Chat] [WhatsApp?] [Telegram?]
 *
 * Voice-кнопка скрывается у text-only дилеров (`showVoiceButton={false}`, #1353).
 */

import { memo, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import { VoiceIcon, ChatIcon, WhatsappIcon, TelegramIcon } from './icons';
import { MorphIcon } from './MorphIcon';
import { t } from './translations';
import { languagePropType } from './utils/propTypes';
import './CollapsedBar.css';

export const CollapsedBar = memo(({
  // Язык подписей кнопок для скринридера (#1431); у дилеров — en.
  language = 'en',
  title = 'Ask us',
  showWhatsApp = false,
  // #1493 — Telegram рядом с WhatsApp; родитель показывает только при валидном хендле.
  showTelegram = false,
  // #1353 — text-only дилер: голосовой точки входа нет вообще.
  showVoiceButton = true,
  onMicClick,
  onChatClick,
  onWhatsAppClick,
  onTelegramClick,
  primaryColor,
  isAccentMode = false,
  isAccentButtons = false,
  glassEffect = false,
  // Мини-состояние: только иконка + заголовок, без кнопок
  isMini = false,
  onExpand,
}) => {
  // #1493: голос + чат + WhatsApp + Telegram не влезают в 300px бара —
  // при четырёх кнопках бар шире на один слот (см. CollapsedBar.css).
  const actionCount = 1 + Number(showVoiceButton) + Number(showWhatsApp) + Number(showTelegram);
  const barClassName = [
    'collapsed-bar',
    actionCount >= 4 && 'collapsed-bar--wide',
    glassEffect && 'collapsed-bar--glass',
    isAccentButtons && 'collapsed-bar--accent-buttons',
    isMini && 'collapsed-bar--mini',
  ].filter(Boolean).join(' ');

  const barRef = useRef(null);
  const tapTimersRef = useRef([]);

  // Cleanup setTimeout при unmount
  useEffect(() => () => tapTimersRef.current.forEach(clearTimeout), []);

  const handleBarClick = (e) => {
    if (isMini && onExpand) {
      e.stopPropagation();
      e.preventDefault();
      // Анимация тапа: scale up → вернуть → expand
      if (barRef.current) {
        barRef.current.classList.add('collapsed-bar--mini--tapped');
        const t1 = setTimeout(() => {
          barRef.current?.classList.remove('collapsed-bar--mini--tapped');
          const t2 = setTimeout(() => onExpand(), 80);
          tapTimersRef.current.push(t2);
        }, 120);
        tapTimersRef.current.push(t1);
      } else {
        onExpand();
      }
    }
  };

  /* Клавиатурный путь для свёрнутого бара. Пока кнопки были в дереве
     доступности, развернуть виджет с клавиатуры можно было косвенно: Tab
     доводил до невидимой кнопки, Enter вызывал её onClick (pointer-events
     блокирует мышь, но не активацию с клавиатуры). Убрав кнопки из дерева,
     этот путь надо заменить явным — иначе на телефоне с внешней клавиатурой
     или под VoiceOver свёрнутый виджет становится недостижим совсем (#1329). */
  const handleBarKeyDown = (e) => {
    if (!isMini) return;
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    e.preventDefault();
    handleBarClick(e);
  };

  return (
    <div
      ref={barRef}
      className={barClassName}
      role={isMini ? 'button' : undefined}
      tabIndex={isMini ? 0 : undefined}
      aria-label={isMini ? `${title} — expand` : undefined}
      onKeyDown={isMini ? handleBarKeyDown : undefined}
      onClick={isMini ? handleBarClick : undefined}
      onTouchEnd={isMini ? handleBarClick : undefined}
    >
      {/* Левая часть: иконка + заголовок */}
      <div className="collapsed-bar__left">
        <MorphIcon
          size={40}
          color={isAccentMode ? 'var(--widget-accent-contrast, #ffffff)' : primaryColor}
        />
        <span className="collapsed-bar__title">{title}</span>
      </div>

      {/* Правая часть: кнопки действий (скрыты в mini).
          В mini-состоянии CSS даёт им width/height 0 и `pointer-events: none`
          — мышью не нажать. Но из дерева доступности они не уходили: скринридер
          продолжал объявлять «Start voice conversation», а Tab — ставить фокус
          на невидимую кнопку. `aria-hidden` + `tabIndex={-1}` убирают их
          оттуда, пока бар свёрнут; при разворачивании всё возвращается (#1329). */}
      <div className="collapsed-bar__actions" aria-hidden={isMini || undefined}>
        {/* Голос — скрыт целиком у text-only дилеров (#1353). Именно
            скрыт, а не disabled: кнопка, которую никогда нельзя нажать,
            только шумит в дереве доступности. */}
        {showVoiceButton && (
          <button
            className="collapsed-bar__btn"
            tabIndex={isMini ? -1 : undefined}
            onClick={onMicClick}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label={t('startVoice', language)}
            title="Voice"
          >
            <VoiceIcon size={24} />
          </button>
        )}

        {/* Чат */}
        <button
          className="collapsed-bar__btn"
          tabIndex={isMini ? -1 : undefined}
          onClick={onChatClick}
          onMouseDown={(e) => e.stopPropagation()}
          aria-label={t('openChat', language)}
          title="Chat"
        >
          <ChatIcon size={24} />
        </button>

        {/* WhatsApp (опционально) */}
        {showWhatsApp && (
          <button
            className="collapsed-bar__btn"
            tabIndex={isMini ? -1 : undefined}
            onClick={onWhatsAppClick}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Contact via WhatsApp"
            title="WhatsApp"
          >
            <WhatsappIcon size={24} />
          </button>
        )}

        {/* Telegram (опционально, #1493) */}
        {showTelegram && (
          <button
            className="collapsed-bar__btn"
            tabIndex={isMini ? -1 : undefined}
            onClick={onTelegramClick}
            onMouseDown={(e) => e.stopPropagation()}
            aria-label="Contact via Telegram"
            title="Telegram"
          >
            <TelegramIcon size={24} />
          </button>
        )}
      </div>
    </div>
  );
});

CollapsedBar.displayName = 'CollapsedBar';

CollapsedBar.propTypes = {
  language: languagePropType,
  title: PropTypes.string,
  showWhatsApp: PropTypes.bool,
  showTelegram: PropTypes.bool,
  showVoiceButton: PropTypes.bool,
  onMicClick: PropTypes.func,
  onChatClick: PropTypes.func,
  onWhatsAppClick: PropTypes.func,
  onTelegramClick: PropTypes.func,
  primaryColor: PropTypes.string,
  isAccentMode: PropTypes.bool,
  isAccentButtons: PropTypes.bool,
  glassEffect: PropTypes.bool,
  isMini: PropTypes.bool,
  onExpand: PropTypes.func,
};
